import {
  computed,
  createApp,
  defineComponent,
  h,
  onMounted,
  ref,
  type Component,
  type PropType,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { createPinia } from 'pinia'
import {
  createRouter,
  createWebHistory,
  RouterLink,
  RouterView,
  type RouteRecordRaw,
  useRoute,
} from 'vue-router'
import {
  apiClientKey,
  createApiClient,
} from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import {
  createAppI18n,
  storeLocale,
  type AppLocale,
  type AppLocaleMessages,
} from '@yuksalish/i18n'
import { hasEveryPermission } from '@yuksalish/permissions'
import '@fontsource-variable/inter'
import './styles.css'
export { default as SmartPickerModal } from './SmartPickerModal.vue'
export type { SmartPickerOption } from './SmartPickerModal.vue'
export { useDragSort } from './useDragSort'
export type {
  UseDragSortOptions,
  UseDragSortReturn,
} from './useDragSort'

export interface StandaloneAppConfig {
  code: string
  title: LocalizedText
  description: LocalizedText
  accent: string
  navigation: Array<string | NavigationItem>
  requiredPermissions?: string[]
  routes?: RouteRecordRaw[]
  messages?: AppLocaleMessages
}

export type LocalizedText = string | Partial<Record<AppLocale, string>>

export interface PortalAppTile {
  code: string
  title: LocalizedText
  description: LocalizedText
  href: string
  accent: string
  requiredPermissions: string[]
  icon?: Component
}

export interface PortalAppConfig {
  title: LocalizedText
  description: LocalizedText
  apps: PortalAppTile[]
  messages?: AppLocaleMessages
}

export interface NavigationItem {
  label: LocalizedText
  to: string
  permission?: string
  children?: NavigationItem[]
}

function padDatePart(value: number): string {
  return String(value).padStart(2, '0')
}

function resolveDate(value: string | number | Date): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatDisplayDate(
  value: string | number | Date | null | undefined,
): string {
  if (value == null || value === '') return ''
  const date = resolveDate(value)
  if (!date) return ''
  return `${padDatePart(date.getDate())}.${padDatePart(date.getMonth() + 1)}.${String(date.getFullYear()).slice(-2)}`
}

export function formatDisplayDateTime(
  value: string | number | Date | null | undefined,
): string {
  if (value == null || value === '') return ''
  const date = resolveDate(value)
  if (!date) return ''
  return `${formatDisplayDate(date)} ${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`
}

/**
 * Formats a Date, ISO string, or `datetime-local` value into the backend's
 * strict `Y-m-d H:i:s` timestamp format (e.g. `2026-08-05 09:30:00`).
 * `datetime-local` and `Y-m-d H:i:s` strings are treated as local wall-clock
 * and reformatted without a timezone shift; Date objects use local components.
 */
export function toApiDateTime(
  value: string | number | Date | null | undefined,
): string {
  if (value == null || value === '') return ''
  const text = String(value).trim()
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}:${match[6] ?? '00'}`
  }
  const date = resolveDate(value)
  if (!date) return ''
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())} ${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(date.getSeconds())}`
}

/** Current time formatted for the backend `Y-m-d H:i:s` timestamp fields. */
export function nowApiDateTime(): string {
  return toApiDateTime(new Date())
}

function resolveLocalizedText(
  value: LocalizedText,
  locale: AppLocale,
): string {
  if (typeof value === 'string') {
    return value
  }
  return value[locale] || value.en || value.uz || value.ru || ''
}

export function getRoutePermissions(
  meta: Record<string | number | symbol, unknown>,
): string[] {
  if (Array.isArray(meta.permissions)) {
    return meta.permissions.filter(
      (permission): permission is string => typeof permission === 'string',
    )
  }
  return typeof meta.permission === 'string' ? [meta.permission] : []
}

export function canAccessRoute(
  meta: Record<string | number | symbol, unknown>,
  grantedPermissions: readonly string[],
): boolean {
  return hasEveryPermission(
    grantedPermissions,
    getRoutePermissions(meta),
  )
}

export function canAccessApp(
  app: Pick<PortalAppTile, 'requiredPermissions'>,
  grantedPermissions: readonly string[],
): boolean {
  return hasEveryPermission(grantedPermissions, app.requiredPermissions)
}

const AppHome = defineComponent({
  name: 'AppHome',
  props: {
    config: {
      type: Object as PropType<StandaloneAppConfig>,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    permissions: {
      type: Array as PropType<string[]>,
      required: true,
    },
    onLogout: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
  setup(props) {
    const { t, locale } = useI18n()
    const route = useRoute()
    const changeLocale = (event: Event) => {
      const nextLocale = (event.target as HTMLSelectElement).value as AppLocale
      locale.value = nextLocale
      storeLocale(nextLocale)
      document.documentElement.lang = nextLocale
    }

    const visibleNavigation = computed(() =>
      props.config.navigation.filter(
        (item) =>
          typeof item === 'string' ||
          !item.permission ||
          props.permissions.includes(item.permission),
      ),
    )

    const openGroups = ref(new Set<string>())

    function toggleGroup(key: string): void {
      const next = new Set(openGroups.value)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      openGroups.value = next
    }

    function groupKey(item: NavigationItem): string {
      return typeof item.label === 'string' ? item.label : (item.label.en || item.label.uz || item.label.ru || '')
    }

    function expandActiveGroup(): void {
      const activeGroup = props.config.navigation.find(
        (item): item is NavigationItem =>
          typeof item !== 'string' &&
          Boolean(item.children?.some((child) => child.to === route.path)),
      )
      if (activeGroup) {
        openGroups.value = new Set([...openGroups.value, groupKey(activeGroup)])
      }
    }

    onMounted(expandActiveGroup)

    function renderNavChildren(children: NavigationItem[]): ReturnType<typeof h>[] {
      return children
        .filter(
          (child) =>
            !child.permission ||
            props.permissions.includes(child.permission),
        )
        .map((child) =>
          h(
            RouterLink,
            {
              class: 'nav-subitem',
              to: child.to,
            },
            { default: () => resolveLocalizedText(child.label, locale.value as AppLocale) },
          ),
        )
    }

    function renderNavItem(item: string | NavigationItem, index: number): ReturnType<typeof h> {
      if (typeof item === 'string') {
        return h(
          'button',
          {
            class: ['nav-item', { 'nav-item--active': index === 0 }],
            type: 'button',
          },
          item,
        )
      }

      if (item.children?.length) {
        const key = groupKey(item)
        const open = openGroups.value.has(key)
        return h('div', { class: 'nav-group' }, [
          h(
            'button',
            {
              class: ['nav-group__toggle', { 'nav-group__toggle--open': open }],
              type: 'button',
              'aria-expanded': open,
              onClick: () => toggleGroup(key),
            },
            [
              h('span', resolveLocalizedText(item.label, locale.value as AppLocale)),
              h('span', { class: 'nav-group__chevron', 'aria-hidden': 'true' }, open ? '▾' : '▸'),
            ],
          ),
          open
            ? h('div', { class: 'nav-submenu' }, renderNavChildren(item.children))
            : null,
        ])
      }

      return h(
        RouterLink,
        {
          class: 'nav-item',
          to: item.to,
        },
        { default: () => resolveLocalizedText(item.label, locale.value as AppLocale) },
      )
    }

    return () =>
      h('div', { class: 'app-frame' }, [
        h('aside', { class: 'app-sidebar' }, [
          h('div', { class: 'app-brand' }, [
            h('span', {
              class: 'app-brand__mark',
            }),
            h('div', [
              h('strong', 'Yuksalish ERP'),
              h('span', resolveLocalizedText(props.config.title, locale.value as AppLocale)),
            ]),
          ]),
          h('nav', { 'aria-label': t('common.primaryNavigation') },
            visibleNavigation.value.map(renderNavItem),
          ),
          h('div', { class: 'sidebar-user' }, [
            h('div', { class: 'sidebar-user__identity' }, [
              h('span', { class: 'sidebar-user__avatar', 'aria-hidden': 'true' }, props.userName.trim().charAt(0).toUpperCase() || 'U'),
              h('span', { class: 'user-name', title: props.userName }, props.userName),
            ]),
            h('label', { class: 'sidebar-language' }, [
              h('span', t('common.language')),
              h('select', {
                value: locale.value,
                'aria-label': t('common.language'),
                onChange: changeLocale,
              }, [
                h('option', { value: 'uz' }, 'O‘zbekcha'),
                h('option', { value: 'ru' }, 'Русский'),
                h('option', { value: 'en' }, 'English'),
              ]),
            ]),
            h(
              'button',
              {
                class: 'sidebar-signout',
                type: 'button',
                onClick: props.onLogout,
              },
              t('common.signOut'),
            ),
          ]),
        ]),
        h('main', { class: 'app-main' }, [
          props.config.routes?.length
            ? h('section', { class: 'app-content' }, [h(RouterView)])
            : h('section', { class: 'app-content' }, [
                h('h2', 'Workspace ready'),
                h('p', resolveLocalizedText(props.config.description, locale.value as AppLocale)),
                h('div', { class: 'module-grid' }, [
                  ...props.config.navigation.map((item) =>
                    h('article', { class: 'module-tile' }, [
                      h('span', {
                        class: 'module-tile__accent',
                      }),
                      h('h3', typeof item === 'string' ? item : resolveLocalizedText(item.label, locale.value as AppLocale)),
                      h('p', 'Feature module boundary'),
                    ]),
                  ),
                ]),
              ]),
        ]),
      ])
  },
})

export function mountStandaloneApp(config: StandaloneAppConfig): void {
  const pinia = createPinia()
  const auth = useAuthStore(pinia)
  const client = createApiClient({
    baseURL: import.meta.env.DEV
      ? (
          String(import.meta.env.VITE_API_DEV_BASE_URL ?? '').trim() ||
          '/api/v1'
        )
      : (
          String(import.meta.env.VITE_API_BASE_URL ?? '').trim() ||
          'https://api.yuksalish.apnet.uz/api/v1'
        ),
    getAccessToken: () => auth.token,
    onUnauthorized: () => auth.clearSession(),
  })

  const ProtectedRoot = defineComponent({
    name: 'ProtectedRoot',
    setup() {
      const { t, locale } = useI18n()
      const route = useRoute()
      const email = ref('')
      const password = ref('')

      onMounted(() => auth.bootstrap(client))

      async function submitLogin(): Promise<void> {
        await auth.login(client, {
          email: email.value.trim(),
          password: password.value,
        })
      }

      return () => {
        if (auth.isBootstrapping) {
          return h('main', { class: 'state-screen' }, [
            h('div', { class: 'state-panel' }, [
              h('span', { class: 'state-loader', 'aria-hidden': 'true' }),
              h('p', t('common.validating')),
            ]),
          ])
        }

        if (!auth.isAuthenticated) {
          return h('main', { class: 'login-screen' }, [
            h('form', {
              class: 'login-panel',
              onSubmit: (event: Event) => {
                event.preventDefault()
                void submitLogin()
              },
            }, [
              h('div', { class: 'login-brand' }, [
                h('span', {
                  class: 'app-brand__mark',
                }),
                h('div', [
                  h('strong', 'Yuksalish ERP'),
                  h('span', resolveLocalizedText(config.title, locale.value as AppLocale)),
                ]),
              ]),
              h('h1', t('common.signIn')),
              h('label', { for: 'email' }, t('common.email')),
              h('input', {
                id: 'email',
                type: 'email',
                autocomplete: 'username',
                required: true,
                value: email.value,
                onInput: (event: Event) => {
                  email.value = (event.target as HTMLInputElement).value
                },
              }),
              h('label', { for: 'password' }, t('common.password')),
              h('input', {
                id: 'password',
                type: 'password',
                autocomplete: 'current-password',
                required: true,
                value: password.value,
                onInput: (event: Event) => {
                  password.value = (event.target as HTMLInputElement).value
                },
              }),
              auth.errorMessage
                ? h('p', { class: 'form-error', role: 'alert' }, auth.errorMessage)
                : null,
              h(
                'button',
                {
                  class: 'primary-button',
                  type: 'submit',
                  disabled: auth.isSubmitting,
                },
                auth.isSubmitting ? t('common.signingIn') : t('common.signIn'),
              ),
            ]),
          ])
        }

        const permissions = auth.user?.permissions ?? []
        if (
          !hasEveryPermission(permissions, config.requiredPermissions ?? [])
        ) {
          return h('main', { class: 'state-screen' }, [
            h('div', { class: 'state-panel state-panel--wide' }, [
              h('p', { class: 'app-code' }, config.code),
              h('h1', t('common.accessDenied')),
              h(
                'p',
                t('common.noAppPermission'),
              ),
              h(
                'button',
                {
                  class: 'text-button',
                  type: 'button',
                  onClick: () => void auth.logout(client),
                },
                t('common.signOut'),
              ),
            ]),
          ])
        }

        if (!canAccessRoute(route.meta, permissions)) {
          return h('main', { class: 'state-screen' }, [
            h('div', { class: 'state-panel state-panel--wide' }, [
              h('p', { class: 'app-code' }, config.code),
              h('h1', t('common.accessDenied')),
              h(
                'p',
                t('common.noPagePermission'),
              ),
              h(RouterLink, { to: '/' }, { default: () => t('common.returnOverview') }),
            ]),
          ])
        }

        return h(AppHome, {
          config,
          userName: auth.user?.name ?? t('common.user'),
          permissions,
          onLogout: () => void auth.logout(client),
        })
      }
    },
  })

  const router = createRouter({
    history: createWebHistory(),
    routes: [
      {
        path: '/',
        component: ProtectedRoot,
        children: [
          ...(config.routes ?? []),
          {
            path: 'access-denied',
            name: 'access-denied',
            component: defineComponent({
              setup: () => () =>
                h('div', { class: 'route-state' }, [
                  h('p', { class: 'app-code' }, config.code),
                  h('h2', 'Access denied'),
                  h(
                    'p',
                    'Your account does not have permission to open this page.',
                  ),
                  h(RouterLink, { to: '/' }, { default: () => 'Return to overview' }),
                ]),
            }),
          },
          {
            path: ':pathMatch(.*)*',
            name: 'not-found',
            component: defineComponent({
              setup: () => () =>
                h('div', { class: 'route-state' }, [
                  h('p', { class: 'app-code' }, config.code),
                  h('h2', 'Page not found'),
                  h('p', 'The requested page does not exist in this application.'),
                  h(RouterLink, { to: '/' }, { default: () => 'Return to overview' }),
                ]),
            }),
          },
        ],
      },
    ],
  })

  router.beforeEach((to) => {
    if (
      auth.isAuthenticated &&
      !canAccessRoute(to.meta, auth.user?.permissions ?? [])
    ) {
      return {
        name: 'access-denied',
        query: { from: to.fullPath },
      }
    }

    return true
  })

  const i18n = createAppI18n(config.messages)
  document.documentElement.lang = i18n.global.locale.value

  createApp({ render: () => h(RouterView) })
    .use(pinia)
    .use(i18n)
    .use(router)
    .provide(apiClientKey, client)
    .mount('#app')
}

export function mountPortalApp(config: PortalAppConfig): void {
  const pinia = createPinia()
  const auth = useAuthStore(pinia)
  const client = createApiClient({
    baseURL: import.meta.env.DEV
      ? (
          String(import.meta.env.VITE_API_DEV_BASE_URL ?? '').trim() ||
          '/api/v1'
        )
      : (
          String(import.meta.env.VITE_API_BASE_URL ?? '').trim() ||
          'https://api.yuksalish.apnet.uz/api/v1'
        ),
    getAccessToken: () => auth.token,
    onUnauthorized: () => auth.clearSession(),
  })

  const PortalRoot = defineComponent({
    name: 'PortalRoot',
    setup() {
      const { t, locale } = useI18n()
      const email = ref('')
      const password = ref('')

      onMounted(() => auth.bootstrap(client))

      const accessibleApps = computed(() => {
        const permissions = auth.user?.permissions ?? []
        return config.apps.filter((app) => canAccessApp(app, permissions))
      })

      const changeLocale = (event: Event) => {
        const nextLocale = (event.target as HTMLSelectElement).value as AppLocale
        locale.value = nextLocale
        storeLocale(nextLocale)
        document.documentElement.lang = nextLocale
      }

      async function submitLogin(): Promise<void> {
        await auth.login(client, {
          email: email.value.trim(),
          password: password.value,
        })
      }

      function renderLogin(): ReturnType<typeof h> {
        return h('main', { class: 'login-screen portal-login' }, [
          h('form', {
            class: 'login-panel',
            onSubmit: (event: Event) => {
              event.preventDefault()
              void submitLogin()
            },
          }, [
            h('div', { class: 'login-brand' }, [
              h('span', {
                class: 'app-brand__mark',
              }),
              h('div', [
                h('strong', 'Yuksalish ERP'),
                h('span', resolveLocalizedText(config.title, locale.value as AppLocale)),
              ]),
            ]),
            h('h1', t('common.signIn')),
            h('label', { for: 'email' }, t('common.email')),
            h('input', {
              id: 'email',
              type: 'email',
              autocomplete: 'username',
              required: true,
              value: email.value,
              onInput: (event: Event) => {
                email.value = (event.target as HTMLInputElement).value
              },
            }),
            h('label', { for: 'password' }, t('common.password')),
            h('input', {
              id: 'password',
              type: 'password',
              autocomplete: 'current-password',
              required: true,
              value: password.value,
              onInput: (event: Event) => {
                password.value = (event.target as HTMLInputElement).value
              },
            }),
            auth.errorMessage
              ? h('p', { class: 'form-error', role: 'alert' }, auth.errorMessage)
              : null,
            h(
              'button',
              {
                class: 'primary-button',
                type: 'submit',
                disabled: auth.isSubmitting,
              },
              auth.isSubmitting ? t('common.signingIn') : t('common.signIn'),
            ),
          ]),
        ])
      }

      function renderPortal(): ReturnType<typeof h> {
        const permissions = auth.user?.permissions ?? []

        return h('main', { class: 'portal-shell' }, [
          h('header', { class: 'portal-header' }, [
            h('div', { class: 'portal-brand' }, [
              h('span', { class: 'app-brand__mark' }),
              h('div', [
                h('strong', 'Yuksalish ERP'),
                h('span', resolveLocalizedText(config.title, locale.value as AppLocale)),
              ]),
            ]),
            h('div', { class: 'portal-userbar' }, [
              h('span', { class: 'portal-userbar__name' }, auth.user?.name ?? t('common.user')),
              h('label', { class: 'portal-language' }, [
                h('span', t('common.language')),
                h('select', {
                  value: locale.value,
                  'aria-label': t('common.language'),
                  onChange: changeLocale,
                }, [
                  h('option', { value: 'uz' }, 'O\'zbekcha'),
                  h('option', { value: 'ru' }, 'Русский'),
                  h('option', { value: 'en' }, 'English'),
                ]),
              ]),
              h(
                'button',
                {
                  class: 'text-button',
                  type: 'button',
                  onClick: () => void auth.logout(client),
                },
                t('common.signOut'),
              ),
            ]),
          ]),
          h('section', {
            class: 'portal-grid',
            'aria-label': resolveLocalizedText({
              uz: 'ERP ilovalari',
              ru: 'ERP-приложения',
              en: 'ERP applications',
            }, locale.value as AppLocale),
          },
            config.apps.map((app) => {
              const enabled = canAccessApp(app, permissions)
              const label = resolveLocalizedText(app.title, locale.value as AppLocale)
              const content = [
                app.icon
                  ? h('span', { class: 'portal-card__icon' }, [
                      h(app.icon, { size: 56, strokeWidth: 1.35, 'aria-hidden': 'true' }),
                    ])
                  : null,
                h('h2', label),
                h('p', resolveLocalizedText(app.description, locale.value as AppLocale)),
              ]

              return enabled
                ? h('a', {
                    class: ['portal-card', 'portal-card--active'],
                    href: app.href,
                    'aria-label': label,
                  }, content)
                : h('article', {
                    class: ['portal-card', 'portal-card--disabled'],
                    'aria-disabled': 'true',
                  }, content)
            }),
          ),
          h('footer', { class: 'portal-instructions' }, [
            h(
              'span',
              { class: 'portal-access-count' },
              resolveLocalizedText({
                uz: `${accessibleApps.value.length}/${config.apps.length} ilova mavjud`,
                ru: `${accessibleApps.value.length}/${config.apps.length} приложений доступно`,
                en: `${accessibleApps.value.length}/${config.apps.length} apps available`,
              }, locale.value as AppLocale),
            ),
            h('p', resolveLocalizedText(config.description, locale.value as AppLocale)),
          ]),
        ])
      }

      return () => {
        if (auth.isBootstrapping) {
          return h('main', { class: 'state-screen' }, [
            h('div', { class: 'state-panel' }, [
              h('span', { class: 'state-loader', 'aria-hidden': 'true' }),
              h('p', t('common.validating')),
            ]),
          ])
        }

        return auth.isAuthenticated ? renderPortal() : renderLogin()
      }
    },
  })

  const i18n = createAppI18n(config.messages)
  document.documentElement.lang = i18n.global.locale.value

  createApp(PortalRoot)
    .use(pinia)
    .use(i18n)
    .provide(apiClientKey, client)
    .mount('#app')
}
